import { MenuPopover, MIconButton, SvgIconStyle } from '@bakerweb/components';
import { useSettings } from '@bakerweb/theme';
import { Box, Button, Divider, FormControlLabel, Switch, Tooltip, Typography } from '@mui/material';

import { alpha } from '@mui/material/styles';
import { fetchUserAttributes, signOut } from 'aws-amplify/auth';
import { ChangeEvent, useEffect, useRef, useState } from 'react';

const defaultEnableMenuItems = {
  modernChartTheme: true,
  darkMode: true
};

/**
 * Optional IdP override. When provided, the popover shows `email` and calls
 * `onSignOut` instead of using the default Cognito/Amplify session — used by
 * apps that authenticate with Entra/MSAL (apps/baker). Apps that omit it
 * (client, houston) keep the original Amplify behavior.
 */
export type AccountPopoverAccount = {
  email: string | null;
  onSignOut: () => void;
};

export const AccountPopover = ({
  enableMenuItems = defaultEnableMenuItems,
  account
}: {
  enableMenuItems?: { modernChartTheme: boolean; darkMode: boolean };
  account?: AccountPopoverAccount;
} = {}) => {
  const { modernChartTheme, darkMode } = enableMenuItems;
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [email, setUserEmail] = useState('');
  const { themeMode, chartTheme, onChangeChartTheme, onChangeThemeMode } = useSettings();

  useEffect(() => {
    if (account) {
      setUserEmail(account.email ?? '');
      return;
    }
    const fetchUser = async () => {
      try {
        const currentUser = await fetchUserAttributes();
        if (currentUser.email) {
          setUserEmail(currentUser.email);
        }
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    };

    fetchUser();
  }, [account]);

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleSignOut = () => {
    if (account) {
      account.onSignOut();
    } else {
      signOut();
    }
  };

  const handleChartThemeToggle = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const value = checked ? 'modern' : 'current';
    onChangeChartTheme({ target: { value } } as ChangeEvent<HTMLInputElement>);
  };

  const handleThemeToggle = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const value = checked ? 'dark' : 'light';
    onChangeThemeMode({ target: { value } } as ChangeEvent<HTMLInputElement>);
  };

  return (
    <>
      <Tooltip title="Account">
        <MIconButton
          ref={anchorRef}
          onClick={handleOpen}
          aria-label="Account Popover"
          sx={{
            padding: 0,
            width: 44,
            height: 44,
            color: (theme) => theme.palette.primary.main,
            ...(open && {
              '&:before': {
                zIndex: 1,
                content: "''",
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                position: 'absolute',
                bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72)
              }
            })
          }}
        >
          <SvgIconStyle src={`/assets/icons/navbar/ic_user.svg`} sx={{ width: '80%', height: '80%' }} />
        </MIconButton>
      </Tooltip>

      <MenuPopover open={open} onClose={handleClose} anchorEl={anchorRef.current} sx={{ width: 300 }}>
        <Box sx={{ my: 2, px: 2.5, textAlign: 'left' }}>
          <Typography variant="subtitle1" noWrap>
            {email}
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        {modernChartTheme && (
          <Box sx={{ px: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2">Modern Chart Theme</Typography>
            <FormControlLabel
              control={<Switch checked={chartTheme === 'modern'} onChange={handleChartThemeToggle} />}
              label=""
              labelPlacement="start"
            />
          </Box>
        )}

        {darkMode && (
          <Box sx={{ px: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2">Dark Mode</Typography>
            <FormControlLabel
              control={<Switch checked={themeMode === 'dark'} onChange={handleThemeToggle} />}
              label=""
              labelPlacement="start"
            />
          </Box>
        )}

        <Box sx={{ p: 2, pt: 1.5 }}>
          <Button fullWidth color="inherit" variant="outlined" aria-label="Sign Out" onClick={handleSignOut}>
            Sign Out
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
};
